import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate, useLocation } from 'react-router-dom';
import { MessageSquare, Send, Shield, Clock, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const SupportMessagesPage = () => {
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [directMessages, setDirectMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('contact');
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    
    // Don't fetch if not authenticated (redirect handled in render)
    if (!isAuthenticated) {
      return;
    }
    fetchMessages();
    
    // Poll for new messages every 10 seconds
    const interval = setInterval(fetchMessages, 10000);
    return () => clearInterval(interval);
  }, [authLoading, isAuthenticated]);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Get user's contact form submissions with admin replies
      const contactRes = await axios.get(`${API_URL}/api/contact/user/my-submissions`, { headers });
      
      // Get direct messages from admin
      const directRes = await axios.get(`${API_URL}/api/contact/user/admin-messages`, { headers });
      
      setMessages(contactRes.data.submissions || []);
      setDirectMessages(directRes.data.messages || []);
    } catch (error) {
      console.error('Error fetching support messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      setSending(true);
      const token = localStorage.getItem('token');
      
      await axios.post(
        `${API_URL}/api/contact/submit`,
        {
          name: `${user.firstName} ${user.lastName}`,
          email: user.email,
          subject: 'Follow-up message',
          message: newMessage
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNewMessage('');
      alert('Message sent to support team!');
      await fetchMessages();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: <Clock className="w-4 h-4" /> },
      replied: { bg: 'bg-green-100', text: 'text-green-800', icon: <CheckCircle className="w-4 h-4" /> },
      resolved: { bg: 'bg-blue-100', text: 'text-blue-800', icon: <CheckCircle className="w-4 h-4" /> }
    };
    
    const style = styles[status] || styles.pending;
    
    return (
      <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${style.bg} ${style.text}`}>
        {style.icon}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // ========================================
  // ALL REDIRECT LOGIC IS IN RENDER (declarative)
  // ========================================

  // 1️⃣ Wait for auth to load
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="w-full px-2">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mint-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // 2️⃣ Not authenticated → redirect to login (declarative)
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 3️⃣ Show loading while fetching messages
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="w-full px-2">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mint-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading messages...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="w-full px-2">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-8 h-8 text-navy-500" />
            <h1 className="text-3xl font-bold text-gray-900">Support Messages</h1>
          </div>
          <p className="text-gray-600">View replies from our support team</p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('contact')}
              className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
                activeTab === 'contact'
                  ? 'text-navy-500 border-b-2 border-mint-500'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              My Inquiries ({messages.length})
            </button>
            <button
              onClick={() => setActiveTab('direct')}
              className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
                activeTab === 'direct'
                  ? 'text-navy-500 border-b-2 border-mint-500'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Direct Messages ({directMessages.length})
            </button>
          </div>
        </div>

        {/* Contact Form Submissions Tab */}
        {activeTab === 'contact' && (
          <div className="space-y-6">
            {/* New Message Form */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Send New Message to Support</h3>
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message to the support team..."
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent resize-none"
              />
              <div className="flex justify-end mt-3">
                <button
                  onClick={sendMessage}
                  disabled={sending || !newMessage.trim()}
                  className="flex items-center gap-2 px-6 py-2 bg-mint-500 text-white rounded-lg hover:bg-mint-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" />
                  {sending ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </div>

            {/* Messages List */}
            {messages.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No messages yet</h3>
                <p className="text-gray-600">Your messages to support will appear here</p>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((msg) => (
                  <div key={msg.id} className="bg-white rounded-lg shadow-sm p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">{msg.subject}</h3>
                        <p className="text-sm text-gray-500">{formatDate(msg.createdAt)}</p>
                      </div>
                      {getStatusBadge(msg.status)}
                    </div>
                    
                    {/* Original Message */}
                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">Your Message:</p>
                      <p className="text-gray-800">{msg.message}</p>
                    </div>

                    {/* Admin Replies */}
                    {msg.replies && msg.replies.length > 0 && (
                      <div className="space-y-3">
                        <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                          <Shield className="w-4 h-4 text-navy-500" />
                          Support Team Replies:
                        </p>
                        {msg.replies.map((reply, index) => (
                          <div key={index} className="bg-cream-100 border-l-4 border-mint-500 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-sm font-semibold text-red-900">{reply.adminName}</p>
                              <p className="text-xs text-navy-500">{formatDate(reply.timestamp)}</p>
                            </div>
                            <p className="text-gray-800">{reply.message}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {msg.status === 'pending' && (
                      <p className="text-sm text-gray-500 mt-4 italic">
                        Waiting for support team response...
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Direct Messages Tab */}
        {activeTab === 'direct' && (
          <div className="space-y-4">
            {directMessages.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                <Shield className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No direct messages</h3>
                <p className="text-gray-600">Direct messages from admin will appear here</p>
              </div>
            ) : (
              directMessages.map((msg) => (
                <div key={msg.id} className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                        <Shield className="w-6 h-6 text-navy-500" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-semibold text-gray-900">
                          {msg.fromUserName} <span className="text-navy-500 text-sm">(Official Support)</span>
                        </p>
                        <p className="text-xs text-gray-500">{formatDate(msg.createdAt)}</p>
                      </div>
                      <div className="bg-cream-100 rounded-lg p-4">
                        <p className="text-gray-800">{msg.message}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Help Text */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Our support team typically responds within 24 hours. For urgent matters, 
            please check your email as well.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SupportMessagesPage;
